<template>
  <div class="dynamic-questionnaire-root">
    <div v-bind:class="['modal', showingQuestionsModal ? 'is-active' : '']">
      <div class="modal-background"></div>
      <div class="modal-content">
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">All Questions</p>
            <button class="delete" aria-label="close" v-on:click="() => { this.showingQuestionsModal = false }"></button>
          </header>
          <section class="modal-card-body">
            <ol class="pl-5">
              <li class="mb-5" v-for="question in questions" :key="question.title" v-if="question.category === 'question'">
                {{ question.title }}
              </li>
            </ol>
          </section>
          <footer class="modal-card-foot">
            <button class="button is-dark" @click="() => { showingQuestionsModal = false }">Return to Questionnaire</button>
          </footer>
        </div>
      </div>
      <button class="modal-close is-large" aria-label="close"></button>
    </div>


    <div class="has-background-white pt-4">
      <div class='page-width mr-6'>
        <img :src="advisorLogo"></img>
      </div>

        <p class="has-text-centered title is-4" v-if="ready && overallPromptHtml && showPrompt">
        {{ overallPromptHtml }}
        <a v-on:click="() => { showingQuestionsModal = true }"><i class="fa fa-info-circle"></i></a>
        </p>

      <div class="pt-4" v-if="loaded && !introComplete && introHtml">
          <PublicBehavioralQnrIntroView :context="{advisor_name: advisorName, advisor_logo: advisorLogo}" :startQtrCallbackFn="onCompleteIntro"/>
      </div>
    </div>

    <flow-form
      v-if="ready"
      v-bind:questions="questions"
      v-on:submit="handleSubmit"
      v-on:step="handleStep"
      v-on:answer="handleAnswer"
      class="base3 topBottomPadding5"
    >
      <template v-slot:complete v-if="conclusionHtml">
        <p class="is-size-5 mb-5" v-html="conclusionHtml"></p>
      </template>
    </flow-form>
  </div>
</template>

<script>
import Advisor from '@frontend/dash3/js/shared/Advisor.js';
import FlowForm, { QuestionModel, QuestionType, ChoiceOption, LinkOption } from '@ditdot-dev/vue-flow-form';
import PublicBehavioralQnrIntroView from './PublicBehavioralQnrIntroView.vue';
import { getQuestionnaireQuestions, getPublicQuestionnaireQuestions, postQuestionnaireResponses } from '@frontend/api/questionnaireApi';
import { postSaveProspect } from '@frontend/api/api';
import telemetry from '@frontend/api/Telemetry';

export default {
  name: 'DynamicQuestionnaire',
  components: {
    FlowForm,
    PublicBehavioralQnrIntroView
  },
  data() {
    return {
      questions: [],

      introHtml: null,
      introComplete: false,
      conclusionHtml: null,

      showPrompt: true,
      showingQuestionsModal: false,
      loaded: false,
    };
  },
  props: {
    clientId: String,
    mode: {
      type: String,
      default: 'default', // default, public
    },
    questionnaireType: {
      type: String,
      default: null,
    },
    resultsRedirect: {
      type: Boolean,
      default: false,
    },
    sendNotifications: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    handleSubmit: async function() {
      // When embedded, notify parent site that questionnaire is complete.
      if (window !== window.parent) {
        window.parent.postMessage({
          messageType: 'questionnaire_complete',
        }, '*');
      }

      telemetry.track('Completed Questionnaire', {'Completed Questionnaire': 'yes'});
      const submitPayload = {
        'client_id': this.clientId,
        'notify': this.sendNotifications,
        'answers': {},
      };
      const contactInfo = {};

      this.questions.forEach((question) => {
        if (question?.category === 'question') {
          submitPayload.answers[question.id] = question.answer;
        }
        if (question?.category === 'contact_info') {
          contactInfo[question.id] = question.answer;
        }
      });

      let prospectResponse;
      if (contactInfo && this.mode === 'public') {
        contactInfo['advisor_slug'] = this.$root.advisor.slug;
        // prospectResponse = (await postSaveProspect(contactInfo, this.$root.$data.csrfToken)).data;
        
        const response = await fetch(`/advisordashboard/prospect-create-public/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(contactInfo),
        });
        prospectResponse = await response.json();
        submitPayload.client_id = prospectResponse.prospect.id;
      }
      if (this.questionnaireType === 'risk') {
        submitPayload.provides_values = false;
      }
      const responseData = (await postQuestionnaireResponses(
        this.questionnaireType || this.$root.advisor.default_questionnaire_type_id,
        submitPayload,
        this.$root.$data.csrfToken
      )).data;
      this.$emit('updated', responseData);

      if (this.resultsRedirect && this.questionnaireType !== 'risk') {
        if (this.mode === 'public') {
          window.location.href = `/questionnaire/intro/results/${this.$root.advisor.link_slug}/${prospectResponse.token}/`;
        }
        else {
          const signedToken = window.location.pathname.split('/').filter(a => a).slice(-1)[0];
          window.location.href = `/questionnaire/results/${signedToken}/`;
        }
      }
      if (this.resultsRedirect && this.questionnaireType === 'risk') {
        let riskResultUrl = '';
        if (this.mode === 'public') {
          riskResultUrl = `/ui/visitor/risk/results/${prospectResponse?.token || responseData.results.client.id}/`;
        }
        else {
          const signedToken = window.location.pathname.split('/').filter(a => a).slice(-1)[0];
          riskResultUrl = `/ui/visitor/risk/results/${signedToken}/`;
        }

        // get checkup_id query param from the url
        // if it exists, append it to the url
        const checkupId = window.location.search.split('checkup_id=')[1];
        if (checkupId) {
          riskResultUrl += `?checkup_id=${checkupId}`;
        }
        window.location.href = riskResultUrl;
      }
    },
    handleAnswer(question) {
      telemetry.track('Question Answered', {
        question,
      });
      console.log('QUESTIONNAIRE TYPE', this.questionnaireType);
      if (question.internalType === 'end-flow'
          && question.options.filter((i) => i.value === question.answer)[0]?.label === 'Not important') {
        window.location.href = `/questionnaire/${this.$root.advisor.link_slug}/offramp/`;
      }
    },
    handleStep(questionId, question) {
      if (question?.category === 'contact_info') {
        this.showPrompt = false;
      }
    },
    onCompleteIntro() {
      this.introComplete = true;
    }
  },
  computed: {
    ready() {
      return this.loaded && (this.introComplete || !this.introHtml);
    }
  },
  created: async function() {
    console.log('QUESTIONNAIRETYPE', this.questionnaireType);
    this.$root.$data.csrfToken = document.querySelector('[name="csrfmiddlewaretoken"]').value;
    this.$root.advisor = this.$root.advisor ? this.$root.advisor : new Advisor();
    const questionnaireData = (await getPublicQuestionnaireQuestions(
      this.questionnaireType || this.$root.advisor.default_questionnaire_type_id,
      this.$root.advisor.link_slug,
    )).data.questionnaire;
    console.log('QUESTIONNAIRE DATA', questionnaireData);
    const questions = questionnaireData.questions;
    this.overallPromptHtml = questionnaireData.meta_info?.overall_prompt;
    this.introHtml = questionnaireData.meta_info?.intro;
    this.conclusionHtml = questionnaireData.meta_info?.conclusion;

    QuestionModel.prototype.internalType = null;

    questions.forEach((itm) => {
      if (itm.type == "option-select") {
          this.questions.push(
            new QuestionModel({
              title: itm.prompt,
              subtitle: itm.subprompt,
              type: QuestionType.MultipleChoice,
              id: itm.slug,
              category: 'question',
              options: itm.choices.map((choice, i) => new ChoiceOption({label: choice, value: i + 1}))
            },)
          );
        } 
        else if (itm.type == "key-value") {
          this.questions.push(
            new QuestionModel({
              title: itm.prompt,
              subtitle: itm.subprompt,
              type: QuestionType.MultipleChoice,
              id: itm.slug,
              category: 'question',
              internalType: itm.type,
              required: itm.required,
              options: Object.entries(itm.choices).map((choice, i) => new ChoiceOption({label: choice[0], value: choice[1]}))
            },)
          );
        }
        else if (itm.choices && ["likert","end-flow"].indexOf(itm.type) > -1) {
          this.questions.push(
            new QuestionModel({
              title: itm.prompt,
              subtitle: itm.subprompt,
              type: QuestionType.MultipleChoice,
              id: itm.slug,
              category: 'question',
              internalType: itm.type,
              options: itm.choices.map((choice, i) => new ChoiceOption({label: choice, value: itm.choices.length - i}))
            },)
          );
        } else {
          this.questions.push(
            new QuestionModel({
              title: itm.prompt,
              type: QuestionType.MultipleChoice,
              id: itm.slug,
              category: 'question',
              options: [
                new ChoiceOption({label: 'Strongly Agree', value: 5}),
                new ChoiceOption({label: 'Agree', value: 4}),
                new ChoiceOption({label: 'Neutral', value: 3}),
                new ChoiceOption({label: 'Disagree', value: 2}),
                new ChoiceOption({label: 'Strongly Disagree', value: 1}),
              ],
            }),
          );
        }
    });

    if (this.mode === 'public') {
      if (!this.$root.advisor.low_pii_questionnaire) {
        this.questions.push(
            new QuestionModel({
            subtitle: 'Please enter your first name',
            id: 'first_name',
            category: 'contact_info',
            type: QuestionType.Text,
            placeholder: 'First Name',
            required: true 
            }),
            new QuestionModel({
            subtitle: 'Please enter your last name',
            id: 'last_name',
            category: 'contact_info',
            type: QuestionType.Text,
            placeholder: 'Last Name',
            required: true
            }),
        );
      }
      this.questions.push(
        new QuestionModel({
          subtitle: 'Please enter your email address',
          id: 'email',
          category: 'contact_info',
          type: QuestionType.Email,
          placeholder: 'Email',
          required: true,
        })
      );
      
      this.questions.push(
        new QuestionModel({
          title: 'Consent',
          subtitle: `
            I have read and agree to the YourStake.org Privacy Policy and Terms of Use, and agree to be contacted by
            email at this address by ${this.$root.advisor.firm_name} and/or their representatives. To stop receiving emails, you
            may opt out at any time by contacting ${this.$root.advisor.firm_name}.
          `,
          type: QuestionType.MultipleChoice,
          multiple: false,
          required: true,
          helpTextShow: false,
          helpText: `
            Your responses will stay between you and ${this.$root.advisor.firm_name}. YourStake does not share your personal data with any third parties.
            `,
          options: [
            new ChoiceOption({
              id: 'marketing_consent',
              label: 'I Agree',
              value: 'on'
            }),
          ],
          descriptionLink: [
            new LinkOption({
              url: '/privacy/',
              text: 'Privacy Policy',
            }),
            new LinkOption({
              url: '/terms-of-use/',
              text: 'Terms of Use',
            }),
          ],
        })
      );
    }
    this.$nextTick(() => {
      this.loaded = true;
      telemetry.track('Questionnaire Started', {'Questionnaire Started': 'yes'});
    });
  },
};
</script>

<style lang="scss">
.vff span.f-sub span+span.f-help  {
  margin-top: 1rem;
}
.vff p.f-description {
  margin-bottom: 1rem;
}
</style>
